import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop — al cambiar de ruta, sube la página al principio.
 *
 * Reglas:
 * - Si la URL trae hash (ej: /servicios#neumaticos) NO forzamos scroll top.
 *   Si el elemento con ese id existe, hacemos scrollIntoView; si no, dejamos
 *   que el navegador resuelva el ancla por defecto.
 * - Si no hay hash, scroll instantáneo a (0,0) en cuanto cambia el pathname.
 * - Usamos `behavior: "instant"` cuando está disponible y caemos a setear
 *   directamente `scrollTop` como fallback (Safari antiguo, ciertos webviews).
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Caso con ancla: respetar el hash
    if (hash) {
      // Esperar 1 frame para que el DOM esté montado en la nueva ruta
      requestAnimationFrame(() => {
        const id = hash.replace(/^#/, "");
        const el = id ? document.getElementById(id) : null;
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
      return;
    }

    // Caso normal: scroll al top inmediato
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      // Fallback para navegadores que no soportan "instant"
      window.scrollTo(0, 0);
    }
    // Algunos layouts usan un contenedor scrollable distinto a window
    // (admin panel). Reseteamos los más comunes por seguridad.
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [pathname, hash]);

  return null;
}
